import { DEFAULT_IMAGE_PLAN } from "../data/catalogue";
import type { ImagePlanSlot, Platform } from "../types/workspace";

type PlatformDefinition = {
  requiredCount: number;
  rules: string[];
};

const PLATFORM_DEFINITIONS: Record<string, PlatformDefinition> = {
  "amazon-jp": {
    requiredCount: 7,
    rules: [
      "Actual product only on pure white; no added text, graphics, props, or watermark.",
      "Show one supplied product feature clearly in a close detail view.",
      "Show a different supplied feature with a distinct composition.",
      "Use a believable context without implying unsupported inclusions.",
      "Communicate scale without inventing measurements.",
      "Demonstrate a realistic use case without unsupported performance claims.",
      "Show only confirmed packaging, accessories, and components."
    ]
  },
  "shopee-tw": {
    requiredCount: 9,
    rules: [
      "Keep the cover image product-led and free of added promotional text.",
      "Visualize one supplied customer benefit.",
      "Communicate one supplied feature through the product or a detail view.",
      "Use a new composition for a different supplied feature.",
      "Reveal the actual material, surface, texture, or finish.",
      "Communicate scale without inventing dimensions.",
      "Show only confirmed bundle, packaging, and accessories.",
      "Use a fresh camera angle that reveals another useful product view.",
      "Show a believable use moment without unsupported claims."
    ]
  },
  rakuten: {
    requiredCount: 7,
    rules: [
      "Present the complete product clearly on a clean, simple background.",
      "Show one supplied feature without unsupported specifications.",
      "Communicate actual material, construction, or finish.",
      "Communicate scale without inventing dimensions.",
      "Show a different supplied feature with an original layout.",
      "Place the product in a believable typical-use environment.",
      "Show only confirmed packaging, accessories, and components."
    ]
  }
};

const fallbackDefinition: PlatformDefinition = {
  requiredCount: 6,
  rules: [
    "Keep the product unmistakably visible and accurate.",
    "Show one supplied customer benefit.",
    "Show a different supplied feature with a new composition.",
    "Reveal actual material and finish.",
    "Communicate scale without invented dimensions.",
    "Show a believable use context without unsupported claims."
  ]
};

export function compilePlanForPlatform(platform: Platform, imageCount = 10): ImagePlanSlot[] {
  const definition = PLATFORM_DEFINITIONS[platform.id] ?? fallbackDefinition;
  const enabledCount = Math.max(1, Math.min(10, imageCount));
  return DEFAULT_IMAGE_PLAN.map((slot, index) => ({
    ...slot,
    number: index + 1,
    enabled: index < enabledCount,
    platformRequired: index < definition.requiredCount,
    platformRule: definition.rules[index]
  }));
}

export function compileEnabledSlots(plan: ImagePlanSlot[], imageCount: number): ImagePlanSlot[] {
  const enabled = plan.filter((slot) => slot.enabled);
  if (enabled.length < imageCount) throw new Error(`Only ${enabled.length} slots enabled, but ${imageCount} images requested`);
  return enabled.slice(0, imageCount);
}
